// CSV encoding utilities with automatic encoding detection
// Handles UTF-8, Windows-1252, MacRoman (Excel Mac), ISO-8859-1 and BOM detection
// Created to fix accent corruption issues in CSV imports
// CHANGE: Removed all debug console statements for production

/**
 * Reads a CSV file with automatic encoding detection
 * Tries encodings in this order:
 * 1. UTF-8 (modern standard)
 * 2. Windows-1252 (Excel Windows)
 * 3. MacRoman/macintosh (Excel Mac)
 * 4. ISO-8859-1 (fallback)
 * Also handles UTF-8 BOM (Byte Order Mark) if present
 */
export async function readCSVFileWithEncoding(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (!arrayBuffer) {
          reject(new Error('Failed to read file'));
          return;
        }

        // Check for UTF-8 BOM (EF BB BF)
        const bytes = new Uint8Array(arrayBuffer);
        let offset = 0;

        if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
          offset = 3;
        }

        // Try UTF-8 decoding first
        try {
          const decoder = new TextDecoder('utf-8', { fatal: true });
          const content = decoder.decode(bytes.slice(offset));

          // Validate that the content doesn't contain replacement characters
          // which would indicate encoding issues
          if (!content.includes('\uFFFD')) {
            resolve(content);
            return;
          }
        } catch (utfError) {
        }

        // Fallback to Windows-1252 (Latin-1)
        // This encoding is commonly used by Excel on Windows
        try {
          const decoder = new TextDecoder('windows-1252');
          const content = decoder.decode(bytes.slice(offset));
          resolve(content);
        } catch (windows1252Error) {
          // Try MacRoman encoding (used by Excel on Mac)
          try {
            const decoder = new TextDecoder('macintosh');
            const content = decoder.decode(bytes.slice(offset));
            resolve(content);
          } catch (macError) {
            // Last resort: try ISO-8859-1
            try {
              const decoder = new TextDecoder('iso-8859-1');
              const content = decoder.decode(bytes.slice(offset));
              resolve(content);
            } catch (finalError) {
              reject(new Error('Unable to decode file with any supported encoding (UTF-8, Windows-1252, MacRoman, ISO-8859-1)'));
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Adds UTF-8 BOM to a string
 * This ensures Excel and other tools recognize the file as UTF-8
 */
export function addUTF8BOM(content: string): string {
  return '\uFEFF' + content;
}

/**
 * Creates a downloadable CSV file with UTF-8 BOM
 * This ensures proper encoding when opening in Excel
 */
export function createCSVBlob(content: string): Blob {
  const contentWithBOM = addUTF8BOM(content);
  return new Blob([contentWithBOM], { type: 'text/csv;charset=utf-8;' });
}
